import { create } from 'zustand';

import { api, ApiError, configureApi, request } from '../lib/api';
import { disconnectRealtime } from '../lib/realtime';
import { useFavorites } from './favorites';
import { useReposts } from './reposts';

/**
 * Who is signed in.
 *
 * ## The access token lives here and only here
 *
 * In a Zustand store, which is to say in memory. Never `localStorage`, never a
 * non-httpOnly cookie: both are readable by any script, so an XSS that reaches
 * either walks away with a working session (HANDOFF §7.1).
 *
 * The consequence is that a page reload loses the token, which is correct and is
 * what `bootstrap()` handles - the durable half of the session is the httpOnly
 * refresh cookie, and it mints a new access token on every load. That is the
 * whole design: the long-lived credential is one the page cannot read, and the
 * one the page holds is worth fifteen minutes.
 *
 * Nothing here is persisted. If you find yourself adding `persist()`, the
 * question to ask first is what an XSS would get.
 */

export interface User {
  id: number;
  username: string;
  display_name: string;
  verified?: boolean;
  email: string;
  email_verified: boolean;
  status: string;
  created_at: string;

  /**
   * Whether to be told when a host you follow goes on air.
   *
   * On the session payload rather than the public profile, because that is what
   * it is: nobody else's business. Optional because the type is shared with
   * responses that carry no session.
   */
  notify_live?: boolean;
  /**
   * Whether a short clip of your public tracks may play inside a chat window.
   *
   * Session-only for the same reason as the switch above: it is a statement
   * about what may be done with your recordings, not something a visitor to
   * your profile has any business reading.
   */
  share_clips?: boolean;
  /**
   * The mute in force, if any: the reason a moderator wrote and when it
   * ends (null for "until lifted"). The shell shows a banner; the routes
   * that would refuse them refuse anyway. Null nearly always.
   */
  mute?: { reason: string; expires_at: string | null } | null;
  /** Whether the profile draws each follow card. */
  /** How many people a rewind reaches. See `RewindComposer`. */
  follower_count?: number;
  show_followers?: boolean;
  show_following?: boolean;
  /** Whether you appear as a face in the rooms you listen to. */
  show_in_crowd?: boolean;
  /** Whether friends (mutual follows) see what you are listening to. */
  share_listening?: boolean;
  /** Whether anybody else can tell they are here. */
  show_online?: boolean;
  /** The character they built, or null for the one seeded from their name. */
  character?: unknown;

  /**
   * Role names and resolved permission keys, from `GET /me`.
   *
   * **A hint for the UI and nothing else.** They decide whether to render an
   * admin link, not whether an action is allowed - every admin route enforces
   * its own permission server-side (HANDOFF §6.7). A client that lies to itself
   * about this list gets a prettier menu and exactly the same 403s.
   *
   * `/auth/login`, `/auth/refresh` and `/me` all resolve the set, so a page
   * reload picks up a permission granted since sign-in. Optional only because
   * the type is shared with responses that have no session attached.
   *
   * The corollary is worth knowing: a permission added to a role while somebody
   * is signed in does not reach their open tab. The server resolves it
   * immediately, the client keeps whatever it was handed at its last renewal,
   * and the UI stays hidden until the page is reloaded. That is a reload, not a
   * bug report.
   */
  roles?: string[];
  permissions?: string[];

  /**
   * Feature flags that are currently on, from `config/features.php`.
   *
   * The same hint-only rule as `permissions` above: this decides whether a
   * control is drawn, never whether the thing behind it is allowed. Every
   * flagged route carries `RequireFeature` and answers 404 regardless of what
   * the client believes.
   *
   * Only the enabled ones are sent. A flag that is off is absent rather than
   * false, so nothing here tells a signed-in user what is being built.
   */
  features?: string[];

  /** Small size only - this drives the header control and nothing else. */
  avatar?: string | null;
}

/** Whether the signed-in user holds a permission. Presentation only - see above. */
export function useCan(permission: string): boolean {
  return useSession((s) => s.user?.permissions?.includes(permission) ?? false);
}

/** Whether a feature flag is on. Presentation only - see above. */
export function useFeature(flag: string): boolean {
  return useSession((s) => s.user?.features?.includes(flag) ?? false);
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

/* ------------------------------------------------------------------------- */
/* Renewal                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * The one refresh call, shared by every caller.
 *
 * **This being single-flight is not an optimisation - it is a correctness
 * requirement**, and getting it wrong logs users out of their own app.
 *
 * Refresh tokens rotate: presenting one consumes it and issues its successor.
 * The server therefore treats a token presented twice as stolen and revokes the
 * whole family (§7.1), which is exactly the behaviour you want against an
 * attacker and exactly what a racing client triggers against itself. Two
 * concurrent refreshes means the second presents a token the first has already
 * rotated, the family dies, and the user is signed out for no reason.
 *
 * Two ordinary things cause that race:
 *
 *   - React StrictMode double-invokes effects in development, so `bootstrap()`
 *     fires twice on every load;
 *   - several queries on one page each get a 401 when the access token expires.
 *
 * Both funnel through here, so at most one request is ever in flight and
 * everybody else awaits the same promise. This was a real bug caught by the
 * browser test, not a hypothetical - the server log said
 * "refresh token reuse detected" on every page reload.
 */
let renewal: Promise<boolean> | null = null;

function renewSession(): Promise<boolean> {
  renewal ??= (async () => {
    try {
      const data = await request<LoginResponse>('/auth/refresh', {
        method: 'POST',
        // No access token to attach, and a 401 here must not trigger another
        // refresh - that is this function.
        anonymous: true,
      });

      useSession.setState({ user: data.user, accessToken: data.access_token });

      return true;
    } catch {
      useSession.setState({ user: null, accessToken: null });

      return false;
    }
  })().finally(() => {
    // Cleared so a later expiry can renew again. Holding a resolved promise
    // here would make the session unrenewable for the rest of the page's life.
    renewal = null;
  });

  return renewal;
}

/* ------------------------------------------------------------------------- */
/* Store                                                                      */
/* ------------------------------------------------------------------------- */

interface SessionState {
  user: User | null;
  accessToken: string | null;

  /**
   * False until the first renewal attempt has resolved.
   *
   * The app must not render routes before this settles. Rendering "signed out"
   * and then flipping bounces a returning visitor off a protected page for a
   * frame, which looks like a bug and loses their deep link.
   */
  ready: boolean;

  bootstrap: () => Promise<void>;
  /** `extra`: the human check's token, when the deployment asks for one. */
  login: (email: string, password: string, extra?: { turnstile?: string }) => Promise<void>;
  /**
   * Take a session from a response some other endpoint produced.
   *
   * Finishing a Discord sign-up creates the account and opens the session in
   * one call, so there is nothing left to log in with - no password was ever
   * set. Here rather than a `set()` in the component so that every way of
   * becoming signed in goes through this store.
   */
  adopt: (session: LoginResponse) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useSession = create<SessionState>()((set, get) => ({
  user: null,
  accessToken: null,
  ready: false,

  /**
   * Restore the session on page load.
   *
   * A 401 inside is the ordinary case for a first-time visitor, not an error
   * worth surfacing - `renewSession` already turned it into `false`.
   */
  bootstrap: async () => {
    await renewSession();
    set({ ready: true });
  },

  login: async (email, password, extra = {}) => {
    const data = await api.post<LoginResponse>(
      '/auth/login',
      { email, password, ...extra },
      // Nothing to attach and nothing to retry: a 401 from login means the
      // password was wrong, and refreshing in response to that is nonsense.
      { anonymous: true },
    );

    set({ user: data.user, accessToken: data.access_token, ready: true });
  },

  adopt: (session) => {
    set({ user: session.user, accessToken: session.access_token, ready: true });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /*
       * Local state is cleared regardless.
       *
       * If the network is down or the token has already expired, the user still
       * pressed "sign out" and the app must honour it. The cookie is scoped to
       * /api/auth and expires on its own; leaving somebody apparently signed in
       * because a request failed is much the worse outcome.
       */
    }

    set({ user: null, accessToken: null });

    // Otherwise the next person to sign in on this browser inherits the last
    // one's hearts until every list happens to refetch.
    useFavorites.getState().clear();
    useReposts.getState().clear();

    // The socket carries an identity through its auth signatures. Leaving it
    // open across a sign-out means the next account inherits a connection
    // authorised for the last one.
    disconnectRealtime();
  },

  /** Re-read the account after something changes it - verifying an address. */
  refreshUser: async () => {
    if (!get().accessToken) {
      return;
    }

    try {
      set({ user: await api.get<User>('/me') });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        set({ user: null, accessToken: null });
      }
    }
  },
}));

/*
 * Hand the API client what it needs to renew a token mid-flight.
 *
 * Registered at module load rather than in a component: a request can happen
 * before anything mounts, and a bridge wired by an effect is missing exactly
 * when the first request needs it.
 */
configureApi({
  getAccessToken: () => useSession.getState().accessToken,
  refresh: renewSession,
  onSessionLost: () => {
    useSession.setState({ user: null, accessToken: null });
    useFavorites.getState().clear();
    useReposts.getState().clear();

    // The socket carries an identity through its auth signatures. Leaving it
    // open across a sign-out means the next account inherits a connection
    // authorised for the last one.
    disconnectRealtime();
  },
});
